import React from 'react';
import {
  Box, Text, VStack, Pressable,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import type { ScheduledChangeDetail } from '@/common/api/models';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { formatEnglishDayMonth } from '@/common/utils/formatEnglishDateTime';
import {
  formatScheduledChangeGoLiveClock,
  scheduledChangeDisplayTimeZone,
} from '../ScheduledChangesList';
import { workingModeBannerKeys, workingModeBannerParams } from '../utils/workingModeBannerKeys';

type BannerTokens = {
  bg: string;
  border: string;
  color: string;
};

const BANNER_TOKENS: Record<ScheduledChangeDetail['state'], BannerTokens> = {
  scheduled: { bg: '$bgBrandSubtle', border: '$brandBase', color: '$textOnBrandSubtle' },
  draft_ready: { bg: '$bgBrandSubtle', border: '$brandBase', color: '$textOnBrandSubtle' },
  applying: { bg: '$bgBrandSubtle', border: '$brandBase', color: '$textOnBrandSubtle' },
  live: { bg: '$statusSuccessSubtle', border: '$statusSuccess', color: '$textOnSuccessSubtle' },
  failed: { bg: '$statusWarningSubtle', border: '$statusWarning', color: '$textOnWarningSubtle' },
  superseded: { bg: '$bgSubtle', border: '$borderMedium', color: '$textBody' },
  cancelled: { bg: '$bgSubtle', border: '$borderMedium', color: '$textBody' },
};

interface Props {
  change: ScheduledChangeDetail;
  onOpenVersion?: (version: string) => void;
}

export const ScheduledChangeStateBanner: React.FC<Props> = ({ change, onOpenVersion }) => {
  const { t } = useTranslation();
  const tenantSettings = useTenantSettingsStore((state) => state.settings);
  const displayTimeZone = scheduledChangeDisplayTimeZone(tenantSettings);
  const tokens = BANNER_TOKENS[change.state];
  const dateSource = change.state === 'live'
    ? change.stateChangedAt
    : (change.goLiveAt ?? change.stateChangedAt);
  const date = dateSource ? formatEnglishDayMonth(dateSource) : '';
  const { version } = change;
  const applied = change.appliedVersion ?? version;
  const goLiveClock = change.state === 'scheduled' && change.goLiveAt
    ? formatScheduledChangeGoLiveClock(change.goLiveAt, displayTimeZone)
    : { time: '', zone: '' };

  const isWorkingMode = change.kind === 'working_mode_switch';
  const bannerParams = {
    date,
    version,
    appliedVersion: applied,
    time: goLiveClock.time,
    zone: goLiveClock.zone,
    draftVersion: change.workingMode?.draftVersion ?? '',
  };
  const wmKeys = isWorkingMode ? workingModeBannerKeys(change) : null;
  const headline = t(
    wmKeys?.headline ?? `lecturer.scheduledChanges.banner.${change.state}.headline`,
    isWorkingMode ? workingModeBannerParams(change, { date, ...goLiveClock }) : bannerParams,
  );
  const sentence = t(
    wmKeys?.sentence ?? `lecturer.scheduledChanges.banner.${change.state}.sentence`,
    isWorkingMode ? workingModeBannerParams(change, { date, time: goLiveClock.time, zone: goLiveClock.zone }) : {
      date,
      version,
      appliedVersion: applied,
    },
  );

  return (
    <Box
      bg={tokens.bg}
      borderLeftWidth={3}
      borderLeftColor={tokens.border}
      borderRadius="$sm"
      px="$3.5"
      py="$3"
    >
      <VStack space="xs">
        <Text size="sm" fontFamily="$heading" color={tokens.color}>
          {headline}
        </Text>
        <Text size="sm" color={tokens.color}>
          {sentence}
        </Text>
        {change.state === 'live' && !!change.appliedVersion && !!onOpenVersion && (
          <Pressable onPress={() => onOpenVersion(change.appliedVersion!)}>
            <Text size="sm" color={tokens.color} textDecorationLine="underline">
              {t('lecturer.scheduledChanges.openVersion', { version: change.appliedVersion })}
            </Text>
          </Pressable>
        )}
      </VStack>
    </Box>
  );
};
